import { useEffect, useState } from "react";
import { useNavigate, Navigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { setLanguage } from "@/lib/i18n";
import {
    Mail, Lock, Eye, EyeOff, ShieldCheck, Loader2, Calendar, Users, Globe,
    ArrowLeft, CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const HERO_IMAGE = "https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";

/**
 * Unified authentication shell.
 * The left brand panel stays constant; the right panel swaps between:
 *   - "signin"  (default)
 *   - "forgot"  (email input)
 *   - "sent"    (post-forgot success screen)
 *   - "reset"   (token + new password, from email link)
 * URL routes /login, /forgot-password, /reset-password all render this file
 * so users always see the branded shell.
 */
export default function Login() {
    const { user, login, refresh, loading } = useAuth();
    const location = useLocation();
    const [params] = useSearchParams();

    // Derive initial view + portal mode from route.
    const isAdminPortal = location.pathname === "/akomoadm";
    const initialView = (() => {
        if (location.pathname === "/forgot-password") return "forgot";
        if (location.pathname === "/reset-password") return "reset";
        return "signin";
    })();
    const [view, setView] = useState(initialView);
    const [resetToken] = useState(params.get("token") || "");

    // IP-based language detection
    useEffect(() => {
        api.get("/i18n/detect").then(({ data }) => {
            if (data?.language) setLanguage(data.language);
        }).catch(() => {});
    }, []);

    if (!loading && user) {
        return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
    }

    return (
        <div className="min-h-screen w-full bg-[#F1F2F4] font-inter-tight flex items-center justify-center p-4 sm:p-6 md:p-8">
            <div className="w-full max-w-[1280px] min-h-[720px] overflow-hidden rounded-[28px] bg-white shadow-[0_30px_80px_-20px_rgba(15,23,42,0.18),0_10px_30px_-15px_rgba(15,23,42,0.12)] grid lg:grid-cols-[minmax(0,42%)_minmax(0,58%)]">
                <BrandPanel />
                <div className="relative flex items-center justify-center bg-white px-6 py-10 sm:px-10 md:px-14 md:py-14 lg:py-16">
                    <div className="w-full max-w-[420px]">
                        <MobileLogo />
                        {view === "signin" && <SignInForm onForgot={() => setView("forgot")} onLogin={login} onRefresh={refresh} isAdminPortal={isAdminPortal} />}
                        {view === "forgot" && <ForgotForm onDone={() => setView("sent")} onBack={() => setView("signin")} />}
                        {view === "sent" && <ForgotSent onBack={() => setView("signin")} />}
                        {view === "reset" && <ResetForm token={resetToken} onBack={() => setView("signin")} onDone={() => setView("signin")} />}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ================= BRAND PANEL (left) =================
function BrandPanel() {
    return (
        <div className="relative hidden overflow-hidden bg-[#252E3D] text-white lg:block">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.32]"
                style={{ backgroundImage: `url("${HERO_IMAGE}")` }}
                aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#252E3D]/95 via-[#252E3D]/80 to-[#252E3D]/60" />

            {/* Curved orange arc + glow */}
            <svg
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -right-32 h-[130%] w-[130%]"
                viewBox="0 0 800 800"
                fill="none"
                preserveAspectRatio="xMinYMin meet"
            >
                <defs>
                    <radialGradient id="akmGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#F26322" stopOpacity="0.65" />
                        <stop offset="60%" stopColor="#F26322" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#F26322" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="akmArc" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#F26322" stopOpacity="0.05" />
                        <stop offset="55%" stopColor="#F26322" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#ff9c5b" stopOpacity="0.95" />
                    </linearGradient>
                </defs>
                <circle cx="600" cy="600" r="360" fill="url(#akmGlow)" />
                <path d="M 40 780 A 720 720 0 0 1 780 40" stroke="url(#akmArc)" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.9" />
                <path d="M -20 820 A 780 780 0 0 1 820 -20" stroke="#F26322" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.35" />
            </svg>

            <div
                className="pointer-events-none absolute inset-0 opacity-[0.035]"
                style={{
                    backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                }}
                aria-hidden
            />

            <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
                <div>
                    <img src="/brand/akomoda-logo-white.png" alt="Akomoda" className="h-12 w-auto select-none" draggable={false} />
                    <div className="mt-2 text-[11px] font-medium tracking-[0.14em] text-white/55">
                        HOTEL PMS &amp; CHANNEL MANAGER
                    </div>
                </div>

                <div className="my-8 max-w-md">
                    <h1 className="text-[36px] font-bold leading-[1.1] tracking-tight xl:text-[42px]">
                        <span className="block text-white">Manage your hotel.</span>
                        <span className="mt-1 block bg-gradient-to-r from-[#F26322] to-[#ff9c5b] bg-clip-text text-transparent">
                            Grow your business.
                        </span>
                    </h1>
                    <p className="mt-5 text-[15px] leading-relaxed text-white/70">
                        All-in-one platform to manage reservations, guests, payments, housekeeping, channels and daily hotel operations from one place.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-5">
                    <Stat icon={Calendar} value="2,450+" label={<>Reservations<br />Today</>} />
                    <Stat icon={Users} value="1,230+" label={<>Hotels Using<br />Akomoda</>} />
                    <Stat icon={Globe} value="150+" label={<>OTA &amp;<br />Integrations</>} />
                </div>

                <div className="mt-8 flex items-start gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 text-white/80">
                        <ShieldCheck size={16} />
                    </div>
                    <div>
                        <div className="text-[13px] font-semibold text-white/90">Secure. Reliable. Always with you.</div>
                        <div className="mt-0.5 text-[13px] font-semibold text-[#F26322]">Akomoda Cloud</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MobileLogo() {
    return (
        <div className="mb-8 flex items-center justify-center lg:hidden">
            <img src="/brand/akomoda-logo-white.png" alt="Akomoda" className="h-9 w-auto invert" />
        </div>
    );
}

function Stat({ icon: Icon, value, label }) {
    return (
        <div>
            <div className="mb-2.5 text-[#F26322]">
                <Icon size={22} strokeWidth={1.75} />
            </div>
            <div className="text-[22px] font-bold leading-none tracking-tight text-white">{value}</div>
            <div className="mt-1.5 text-[11.5px] font-medium leading-tight text-white/60">{label}</div>
        </div>
    );
}

// ================= SIGN IN =================
function SignInForm({ onForgot, onLogin, onRefresh, isAdminPortal = false }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [remember, setRemember] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(() => {
        // Restore any wrong-portal error from a previous forced reload
        const stashed = sessionStorage.getItem("akm_portal_error");
        if (stashed) {
            sessionStorage.removeItem("akm_portal_error");
            return stashed;
        }
        return "";
    });
    // 2FA challenge state — when set, we show the code input instead of email/password
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState("");
    const [rememberDevice, setRememberDevice] = useState(true);
    const navigate = useNavigate();

    const finishLogin = async (userObj) => {
        const role = userObj.role;
        // Wrong portal — revoke the session we just created and show the error inline,
        // without a jarring full-page reload.
        if (isAdminPortal && role !== "super_admin") {
            try { await api.post("/auth/logout"); } catch { /* noop */ }
            localStorage.removeItem("akm_token");
            setPassword("");
            setSubmitting(false);
            setChallenge(null);
            setError("This portal is reserved for administrators. Please sign in at /login instead.");
            return;
        }
        if (!isAdminPortal && role === "super_admin") {
            try { await api.post("/auth/logout"); } catch { /* noop */ }
            localStorage.removeItem("akm_token");
            setPassword("");
            setSubmitting(false);
            setChallenge(null);
            setError("Super Admin accounts sign in from a different URL. Please contact your administrator for the correct portal address.");
            return;
        }
        toast.success(`Welcome back, ${userObj.name}`);
        // Sync the AuthContext user state so ProtectedRoute doesn't bounce us back
        try { await onRefresh?.(); } catch { /* noop */ }
        // Hotel admins with an unfinished wizard go straight to /wizard/{draft_id}.
        if (role === "hotel_admin") {
            try {
                const { data: st } = await api.get("/onboarding/status");
                if (st?.needs_wizard && st?.draft_id) {
                    navigate(`/wizard/${st.draft_id}`, { replace: true });
                    return;
                }
            } catch { /* fall through to dashboard */ }
        }
        navigate(role === "super_admin" ? "/admin" : "/dashboard", { replace: true });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/login", { email: email.trim(), password, remember });
            if (data.requires_2fa) {
                // Move to 2FA step, same panel
                setChallenge(data.challenge);
                setSubmitting(false);
                return;
            }
            if (data.access_token) localStorage.setItem("akm_token", data.access_token);
            await finishLogin(data.user);
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Login failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/2fa/verify", { challenge, code: code.trim(), remember_device: rememberDevice });
            if (data.access_token) localStorage.setItem("akm_token", data.access_token);
            if (data.trusted_device_message) toast.success(data.trusted_device_message);
            await finishLogin(data.user);
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || "Invalid code";
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- 2FA Challenge Step ----------
    if (challenge) {
        return (
            <>
                <BackButton onClick={() => { setChallenge(null); setCode(""); setError(""); }} testid="2fa-back" />
                <h2 className="mt-4 text-center text-[28px] font-bold tracking-tight text-[#0B1220]">Two-factor authentication</h2>
                <p className="mt-2 text-center text-[14.5px] text-slate-500">
                    Enter the 6-digit code from your authenticator app.
                </p>
                <form onSubmit={submitCode} className="mt-7 space-y-3.5" data-testid="2fa-form">
                    <input
                        data-testid="2fa-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
                        placeholder="123456"
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={8}
                        className="h-[62px] w-full rounded-2xl border border-slate-200 bg-white text-center text-[24px] font-mono font-semibold tracking-[0.4em] text-slate-900 outline-none transition placeholder:text-slate-300 focus:border-[#F26322] focus:ring-4 focus:ring-[#F26322]/12"
                    />
                    <p className="text-center text-[12px] text-slate-400">Or use a recovery code if you&apos;ve lost your device</p>
                    <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 select-none">
                        <input
                            type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            data-testid="2fa-remember-device"
                            className="h-4 w-4 rounded border-slate-300 text-[#F26322] accent-[#F26322] focus:ring-2 focus:ring-[#F26322]/25"
                        />
                        <span className="text-[13px] font-medium text-slate-600">Remember this device for 30 days</span>
                    </label>
                    {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700" data-testid="2fa-error">{error}</div>
                    )}
                    <PrimaryButton testid="2fa-submit" submitting={submitting} disabled={code.length < 6}>VERIFY &amp; SIGN IN</PrimaryButton>
                </form>
            </>
        );
    }

    return (
        <>
            {isAdminPortal && (
                <div className="mb-5 flex items-center justify-center gap-2" data-testid="admin-portal-badge">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#F26322]">
                        <ShieldCheck size={12} /> Admin Portal
                    </span>
                </div>
            )}
            <h2 className="text-center text-[30px] font-bold tracking-tight text-[#0B1220]">
                {isAdminPortal ? "Sign in" : "Welcome back"}
            </h2>
            <p className="mt-2 text-center text-[14.5px] text-slate-500">
                {isAdminPortal
                    ? "Sign in to access your Akomoda workspace."
                    : "Sign in to access your Akomoda workspace."}
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-3.5" data-testid="login-form">
                <FieldEmail testid="login-email" value={email} onChange={setEmail} />
                <FieldPassword testid="login-password" value={password} onChange={setPassword} show={showPwd} onToggle={() => setShowPwd(!showPwd)} testidToggle="login-toggle-password" />

                <div className="flex items-center justify-between pt-1">
                    <label className="flex cursor-pointer items-center gap-2 select-none">
                        <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                            data-testid="login-remember"
                            className="h-4 w-4 rounded border-slate-300 text-[#F26322] accent-[#F26322] focus:ring-2 focus:ring-[#F26322]/25"
                        />
                        <span className="text-[13.5px] font-medium text-slate-700">Remember me</span>
                    </label>
                    <button
                        type="button"
                        onClick={onForgot}
                        data-testid="login-forgot"
                        className="text-[13.5px] font-semibold text-[#F26322] transition hover:underline"
                    >
                        Forgot password?
                    </button>
                </div>

                {error && (
                    <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                        {error}
                    </div>
                )}

                <PrimaryButton testid="login-submit" submitting={submitting}>SIGN IN</PrimaryButton>
            </form>

            {!isAdminPortal && (
                <>
                    <OrDivider />

                    <button
                        type="button"
                        data-testid="login-google"
                        onClick={() => toast.info("Google sign-in coming soon")}
                        className="flex h-[52px] w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white text-[14.5px] font-semibold text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-md"
                    >
                        <GoogleIcon />
                        Continue with Google
                    </button>

                    <p className="mt-7 text-center text-[13.5px] text-slate-600">
                        Don&apos;t have an account?{" "}
                        <Link to="/signup" className="font-semibold text-[#F26322] hover:underline" data-testid="login-signup">
                            Create your hotel workspace
                        </Link>
                    </p>
                </>
            )}

            <p className="mt-4 text-center text-[12px] leading-relaxed text-slate-400">
                By signing in you agree to the{" "}
                <a href="https://akomoda.co/terms" target="_blank" rel="noopener noreferrer" className="text-slate-500 underline decoration-slate-300 hover:text-[#F26322] hover:decoration-[#F26322]" data-testid="login-tos">Terms of Service</a>{" "}
                and{" "}
                <a href="https://akomoda.co/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 underline decoration-slate-300 hover:text-[#F26322] hover:decoration-[#F26322]" data-testid="login-privacy">Privacy Policy</a>.
            </p>
        </>
    );
}

// ================= FORGOT PASSWORD =================
function ForgotForm({ onDone, onBack }) {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [lang, setLang] = useState("en");

    useEffect(() => {
        api.get("/i18n/detect").then(({ data }) => setLang(data.language || "en")).catch(() => {});
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim(), lang });
            onDone(email.trim());
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <BackButton onClick={onBack} testid="fp-back" />
            <h2 className="mt-4 text-center text-[30px] font-bold tracking-tight text-[#0B1220]">Forgot password?</h2>
            <p className="mt-2 text-center text-[14.5px] text-slate-500">
                Enter your email and we&apos;ll send you a secure link to reset your password.
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-3.5" data-testid="fp-form">
                <FieldEmail testid="fp-email" value={email} onChange={setEmail} autoFocus />
                <PrimaryButton testid="fp-submit" submitting={submitting}>SEND RESET LINK</PrimaryButton>
            </form>

            <p className="mt-6 text-center text-[13px] text-slate-500">
                Remembered your password?{" "}
                <button onClick={onBack} className="font-semibold text-[#F26322] hover:underline">Sign in</button>
            </p>
        </>
    );
}

function ForgotSent({ onBack }) {
    return (
        <div className="text-center" data-testid="fp-success">
            <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 size={32} />
            </div>
            <h2 className="text-[28px] font-bold tracking-tight text-[#0B1220]">Check your inbox</h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-slate-500">
                If the email you entered is registered with Akomoda, we&apos;ve sent a secure link to reset your password. It expires in 1 hour.
            </p>
            <p className="mt-2 text-[13px] text-slate-400">
                Don&apos;t see it? Check your spam folder or wait a minute — email can be slow sometimes.
            </p>
            <button
                onClick={onBack}
                data-testid="fp-back-signin"
                className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#F26322] hover:underline"
            >
                <ArrowLeft size={14} /> Back to sign in
            </button>
        </div>
    );
}

// ================= RESET PASSWORD =================
function ResetForm({ token, onBack, onDone }) {
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(!token ? "Missing or invalid reset link." : "");

    const rules = {
        length: pwd.length >= 8,
        upper: /[A-Z]/.test(pwd),
        digit: /\d/.test(pwd),
        match: pwd && pwd === pwd2,
    };
    const canSubmit = rules.length && rules.upper && rules.digit && rules.match && !submitting && !!token;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd });
            setDone(true);
            toast.success("Password updated");
            setTimeout(() => onDone(), 2000);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset password");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="text-center" data-testid="rp-success">
                <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-emerald-50 text-emerald-600">
                    <CheckCircle2 size={32} />
                </div>
                <h2 className="text-[28px] font-bold tracking-tight text-[#0B1220]">Password updated</h2>
                <p className="mt-3 text-[14.5px] text-slate-500">Taking you back to sign in…</p>
            </div>
        );
    }

    return (
        <>
            <BackButton onClick={onBack} testid="rp-back" />
            <h2 className="mt-4 text-center text-[28px] font-bold tracking-tight text-[#0B1220]">Set a new password</h2>
            <p className="mt-2 text-center text-[14.5px] text-slate-500">
                Choose a strong password. All your active sessions will be signed out.
            </p>

            <form onSubmit={onSubmit} className="mt-7 space-y-3.5" data-testid="rp-form">
                <FieldPassword testid="rp-new-password" placeholder="New password" value={pwd} onChange={setPwd} show={show} onToggle={() => setShow(!show)} testidToggle="rp-toggle" />
                <FieldPassword testid="rp-confirm-password" placeholder="Confirm new password" value={pwd2} onChange={setPwd2} show={show} onToggle={() => setShow(!show)} testidToggle="rp-toggle-2" />

                <ul className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-[12.5px]">
                    <Rule ok={rules.length}>At least 8 characters</Rule>
                    <Rule ok={rules.upper}>One uppercase letter</Rule>
                    <Rule ok={rules.digit}>One number</Rule>
                    <Rule ok={rules.match}>Passwords match</Rule>
                </ul>

                {error && (
                    <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700" data-testid="rp-error">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                <PrimaryButton testid="rp-submit" submitting={submitting} disabled={!canSubmit}>
                    UPDATE PASSWORD
                </PrimaryButton>
            </form>
        </>
    );
}

// ================= Reusable field components =================
function FieldEmail({ testid, value, onChange, autoFocus = false }) {
    return (
        <div className="group relative">
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-[#F26322]">
                <Mail size={18} />
            </div>
            <input
                data-testid={testid}
                type="email"
                required
                autoFocus={autoFocus}
                autoComplete="email"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Email address"
                className="h-[52px] w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-[15px] text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition placeholder:text-slate-400 focus:border-[#F26322] focus:ring-4 focus:ring-[#F26322]/12"
            />
        </div>
    );
}

function FieldPassword({ testid, placeholder = "Password", value, onChange, show, onToggle, testidToggle }) {
    return (
        <div className="group relative">
            <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-[#F26322]">
                <Lock size={18} />
            </div>
            <input
                data-testid={testid}
                type={show ? "text" : "password"}
                required
                autoComplete="current-password"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="h-[52px] w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-12 text-[15px] text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.03)] outline-none transition placeholder:text-slate-400 focus:border-[#F26322] focus:ring-4 focus:ring-[#F26322]/12"
            />
            <button
                type="button"
                data-testid={testidToggle}
                onClick={onToggle}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-[#F26322]"
            >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        </div>
    );
}

function PrimaryButton({ children, testid, submitting, disabled }) {
    return (
        <button
            data-testid={testid}
            type="submit"
            disabled={submitting || disabled}
            className="mt-2 flex h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-[#F26322] text-[14.5px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_28px_-8px_rgba(242,99,34,0.55)] transition hover:-translate-y-[1px] hover:bg-[#e05715] hover:shadow-[0_16px_36px_-8px_rgba(242,99,34,0.7)] disabled:pointer-events-none disabled:opacity-50"
        >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : children}
        </button>
    );
}

function BackButton({ onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 transition hover:text-[#F26322]"
        >
            <ArrowLeft size={14} /> Back to sign in
        </button>
    );
}

function OrDivider() {
    return (
        <div className="my-5 flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">OR</span>
            <div className="h-px flex-1 bg-slate-200" />
        </div>
    );
}

function Rule({ ok, children }) {
    return (
        <li className="flex items-center gap-1.5">
            <CheckCircle2 size={14} className={ok ? "text-emerald-500" : "text-slate-300"} />
            <span className={ok ? "text-slate-700" : "text-slate-400"}>{children}</span>
        </li>
    );
}

function GoogleIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
    );
}
